import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GripVertical, MapPin, Plus, Edit2, Globe, Search, X, CheckCircle2, UserPlus, Tags, Loader2, RefreshCw, ChevronDown } from 'lucide-react';
import supabase from '../../lib/supabase';
import { useOrgContext } from '../../context/OrgContext';
import { extractCoordinatesFromUrl, isShortMapsUrl } from '../../lib/mapsUtils';

type ProspectStatus = 'new' | 'to_visit' | 'visited_interested' | 'visited_not_interested' | 'nurture' | 'disqualified';

type ProspectContact = {
  id: string;
  company: string | null;
  full_name: string | null;
  phone: string | null;
  email: string | null;
  company_address: string | null;
  company_city: string | null;
  company_zip: string | null;
  google_maps_url: string | null;
  prospect_status: ProspectStatus | null;
  source: string | null;
  created_at: string;
  updated_at: string | null;
  tags?: string[] | null;
  website?: string | null;
};

const prospectStages: ProspectStatus[] = ['new', 'to_visit', 'visited_interested', 'visited_not_interested', 'nurture', 'disqualified'];

export const ProspezionePage: React.FC = () => {
  const { t } = useTranslation();
  
  const stageLabels: Record<ProspectStatus, string> = useMemo(() => ({
    new: t('crm.prospezione.stages.new'),
    to_visit: t('crm.prospezione.stages.to_visit'),
    visited_interested: t('crm.prospezione.stages.visited_interested'),
    visited_not_interested: t('crm.prospezione.stages.visited_not_interested'),
    nurture: t('crm.prospezione.stages.nurture'),
    disqualified: t('crm.prospezione.stages.disqualified'),
  }), [t]);

  // Color scheme for Prospezione (Operational/Physical Action theme)
  const getStageColors = (stage: ProspectStatus) => {
    switch (stage) {
      case 'to_visit':
        return {
          border: 'border-blue-600 dark:border-blue-500',
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          text: 'text-blue-700 dark:text-blue-300',
          headerBg: 'bg-blue-100 dark:bg-blue-900/30',
        };
      case 'visited_interested':
        return {
          border: 'border-orange-500 dark:border-orange-400',
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          text: 'text-orange-700 dark:text-orange-300',
          headerBg: 'bg-orange-100 dark:bg-orange-900/30',
        };
      case 'visited_not_interested':
        return {
          border: 'border-gray-400 dark:border-gray-600',
          bg: 'bg-gray-50 dark:bg-gray-700/50',
          text: 'text-gray-700 dark:text-gray-300',
          headerBg: 'bg-gray-100 dark:bg-gray-700/50',
        };
      case 'nurture':
        return {
          border: 'border-purple-400 dark:border-purple-500',
          bg: 'bg-purple-50 dark:bg-purple-900/20',
          text: 'text-purple-700 dark:text-purple-300',
          headerBg: 'bg-purple-100 dark:bg-purple-900/30',
        };
      case 'disqualified':
        return {
          border: 'border-slate-400 dark:border-slate-600',
          bg: 'bg-slate-50 dark:bg-slate-800/50',
          text: 'text-slate-600 dark:text-slate-400',
          headerBg: 'bg-slate-100 dark:bg-slate-800/50',
        };
      default: // 'new'
        return {
          border: 'border-cyan-400 dark:border-cyan-500',
          bg: 'bg-cyan-50 dark:bg-cyan-900/20',
          text: 'text-cyan-700 dark:text-cyan-300',
          headerBg: 'bg-cyan-100 dark:bg-cyan-900/30',
        };
    }
  };
  const navigate = useNavigate();
  const { orgId, loading: orgLoading, currentUser } = useOrgContext();
  const canEdit = Boolean(currentUser?.canEdit);

  const [contacts, setContacts] = useState<ProspectContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<ProspectStatus | null>(null);
  const [showCheckupModal, setShowCheckupModal] = useState<string | null>(null);
  const [showProspectDrawer, setShowProspectDrawer] = useState(false);
  const [editingProspect, setEditingProspect] = useState<ProspectContact | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagsDropdownOpen, setTagsDropdownOpen] = useState(false);
  const [prospectForm, setProspectForm] = useState({
    company: '',
    full_name: '',
    phone: '',
    email: '',
    company_address: '',
    company_city: '',
    company_zip: '',
    google_maps_url: '',
    website: '',
    source: 'cold_outreach',
    prospect_status: 'new' as ProspectStatus | null,
    lifecycle_stage: 'contact' as 'contact' | 'lead' | 'client',
    tags: '',
  });
  const [contactSearch, setContactSearch] = useState('');
  const [contactOptions, setContactOptions] = useState<any[]>([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [showContactSearch, setShowContactSearch] = useState(false);
  const [mapsUrlExpanding, setMapsUrlExpanding] = useState(false);
  const [mapsUrlExpanded, setMapsUrlExpanded] = useState(false);
  const [coordinatesMessage, setCoordinatesMessage] = useState<{ type: 'success' | 'error' | null; text: string }>({ type: null, text: '' });
  const [selectedStageFilter, setSelectedStageFilter] = useState<ProspectStatus | 'all'>('all'); // Mobile filter chip

  async function fetchContactOptions(term: string) {
    if (!orgId) return;
    setContactLoading(true);
    try {
      const searchTerm = term.trim();
      
      // Build base query
      let query = supabase
        .from('contacts')
        .select('id, full_name, email, phone, company, company_city, tags')
        .eq('org_id', orgId)
        .is('prospect_status', null); // Only show contacts that are not already prospects
      
      if (searchTerm) {
        // Use proper PostgREST or() syntax with ilike filters for case-insensitive search
        // The % wildcards are already in the string, PostgREST will handle them correctly
        query = query.or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%,company.ilike.%${searchTerm}%`);
      }
      
      const { data, error } = await query.limit(20);
      
      if (error) {
        console.error('[Prospezione] Error fetching contact options:', error);
        setContactOptions([]);
      } else {
        setContactOptions(data || []);
      }
    } catch (err) {
      console.error('[Prospezione] Error in fetchContactOptions:', err);
      setContactOptions([]);
    } finally {
      setContactLoading(false);
    }
  }

  useEffect(() => {
    if (!showContactSearch || editingProspect) {
      setContactOptions([]);
      return;
    }
    if (!contactSearch.trim()) {
      setContactOptions([]);
      return;
    }
    const timeout = setTimeout(() => {
      fetchContactOptions(contactSearch);
    }, 250);
    return () => clearTimeout(timeout);
  }, [contactSearch, showContactSearch, editingProspect, orgId]);

  const fetchContacts = useCallback(async () => {
    if (!orgId) {
      setContacts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    
    // Try with website first, fallback without website if column doesn't exist
    // Note: updated_at column doesn't exist, so we use created_at for ordering
    let resp = await supabase
      .from('contacts')
      .select('id, company, full_name, phone, email, company_address, company_city, company_zip, google_maps_url, prospect_status, source, created_at, website, tags, lifecycle_stage')
      .eq('org_id', orgId)
      .not('prospect_status', 'is', null)
      .order('created_at', { ascending: false })
      .limit(500);

    // If error and it's about missing column, try without website
    if (resp.error) {
      const errorMessage = resp.error.message || '';
      if (errorMessage.includes('website') || errorMessage.includes('column') || resp.error.code === 'PGRST116') {
        console.warn('[Prospezione] Website column not found, retrying without website field');
        const fallbackResp = await supabase
          .from('contacts')
          .select('id, company, full_name, phone, email, company_address, company_city, company_zip, google_maps_url, prospect_status, source, created_at, tags, lifecycle_stage')
          .eq('org_id', orgId)
          .not('prospect_status', 'is', null)
          .order('created_at', { ascending: false })
          .limit(500);
        if (!fallbackResp.error && fallbackResp.data) {
          resp = { ...fallbackResp, data: fallbackResp.data.map((contact: any) => ({ ...contact, website: null })) };
        } else {
          resp = fallbackResp;
        }
      }
    }

    if (resp.error) {
      console.error('[Prospezione] Error loading contacts:', resp.error);
      setContacts([]);
    } else {
      // Ensure website and updated_at are set to null if they don't exist
      const contactsWithDefaults = (resp.data || []).map((contact: any) => ({
        ...contact,
        website: contact.website || null,
        updated_at: contact.updated_at || contact.created_at || null, // Use created_at as fallback
      }));
      setContacts(contactsWithDefaults as ProspectContact[]);
    }
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    if (!orgId) {
      setContacts([]);
      setLoading(orgLoading);
      return;
    }
    fetchContacts();
  }, [orgId, orgLoading, fetchContacts]);

  // Close tags dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tagsDropdownOpen && !(event.target as HTMLElement).closest('.relative')) {
        setTagsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [tagsDropdownOpen]);

  // Extract unique first tags (business categories) from contacts
  const tags = useMemo(() => {
    const unique = new Set<string>();
    contacts.forEach((contact) => {
      const firstTag = (contact.tags || [])[0];
      if (firstTag) {
        unique.add(firstTag);
      }
    });
    return Array.from(unique).sort();
  }, [contacts]);

  const grouped = useMemo(() => {
    const groups: Record<ProspectStatus, ProspectContact[]> = {
      new: [],
      to_visit: [],
      visited_interested: [],
      visited_not_interested: [],
      nurture: [],
      disqualified: [],
    };
    const searchLower = searchTerm.trim().toLowerCase();
    
    contacts.forEach((contact) => {
      // Skip contacts without prospect_status (they belong in Contacts screen, not Prospects)
      if (!contact.prospect_status) {
        return;
      }
      
      // Filter by search term if provided
      if (searchLower) {
        const companyMatch = contact.company?.toLowerCase().includes(searchLower) ?? false;
        const fullNameMatch = contact.full_name?.toLowerCase().includes(searchLower) ?? false;
        if (!companyMatch && !fullNameMatch) {
          return; // Skip this contact if it doesn't match the search
        }
      }
      
      // Filter by tags if selected
      if (selectedTags.length > 0) {
        const firstTag = (contact.tags || [])[0];
        if (!firstTag) return; // Skip if no tags
        const tagMatches = selectedTags.some(selectedTag => 
          firstTag.toLowerCase() === selectedTag.toLowerCase()
        );
        if (!tagMatches) return; // Skip if tag doesn't match
      }
      
      const status = contact.prospect_status;
      if (status in groups) {
        groups[status as ProspectStatus].push(contact);
      }
    });
    return groups;
  }, [contacts, searchTerm, selectedTags]);

  const stageTotals = useMemo(() => {
    const totals: Record<ProspectStatus, number> = {
      new: 0,
      to_visit: 0,
      visited_interested: 0,
      visited_not_interested: 0,
      nurture: 0,
      disqualified: 0,
    };
    Object.keys(grouped).forEach((key) => {
      totals[key as ProspectStatus] = grouped[key as ProspectStatus].length;
    });
    return totals;
  }, [grouped]);

  // Calculate KPIs for prospects
  const prospectKpis = useMemo(() => {
    const total = contacts.length;
    const interested = grouped.visited_interested.length;
    const toVisit = grouped.to_visit.length;
    const visited = grouped.visited_interested.length + grouped.visited_not_interested.length;
    const conversionRate = total > 0 ? Math.round((interested / total) * 100) : 0;
    
    // Find prospects that need follow-up (in nurture or new for more than 7 days)
    const sevenDays = 1000 * 60 * 60 * 24 * 7;
    const needsFollowUp = contacts.filter((contact) => {
      const status = contact.prospect_status || 'new';
      if (status === 'nurture') return true;
      if (status === 'new') {
        const daysSinceUpdate = Date.now() - new Date(contact.updated_at || contact.created_at).getTime();
        return daysSinceUpdate > sevenDays;
      }
      return false;
    }).slice(0, 4);

    return {
      total,
      interested,
      toVisit,
      visited,
      conversionRate,
      needsFollowUp,
    };
  }, [contacts, grouped]);

  async function moveProspect(id: string, status: ProspectStatus) {
    if (!canEdit || !orgId) return;
    
      // Note: updated_at column doesn't exist, so we only update prospect_status
      const { error } = await supabase
      .from('contacts')
      .update({ prospect_status: status })
      .eq('id', id)
      .eq('org_id', orgId);

    if (!error) {
      setContacts((prev) =>
        prev.map((contact) => (contact.id === id ? { ...contact, prospect_status: status, updated_at: contact.created_at || new Date().toISOString() } : contact))
      );

      // If moving to to_visit, create a task activity
      if (status === 'to_visit') {
        const { data: auth } = await supabase.auth.getUser();
        const uid = auth.user?.id;
        await supabase.from('activities').insert([
          {
            contact_id: id,
            lead_id: null,
            type: 'task',
            channel: 'web',
            content: t('crm.prospezione.activity.coldVisit'),
            due_at: new Date().toISOString(),
            created_by: uid || null,
            org_id: orgId,
          },
        ]);
      }
    }
  }

  const handleDragStart = (contactId: string) => {
    if (!canEdit) return;
    setDraggingId(contactId);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverStage(null);
  };

  const handleDragOver = (stage: ProspectStatus, event: React.DragEvent<HTMLDivElement>) => {
    if (!canEdit || !draggingId) return;
    event.preventDefault();
    if (dragOverStage !== stage) setDragOverStage(stage);
  };

  const handleDragLeave = (stage: ProspectStatus) => {
    if (dragOverStage === stage) {
      setDragOverStage(null);
    }
  };

  const handleDrop = async (stage: ProspectStatus) => {
    if (!canEdit || !draggingId) return;
    const contact = contacts.find((item) => item.id === draggingId);
    setDragOverStage(null);
    setDraggingId(null);
    if (!contact || contact.prospect_status === stage) return;
    await moveProspect(draggingId, stage);
  };

  async function createCheckup(contactId: string) {
    if (!canEdit || !orgId) return;
    
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact) return;

    // Create lead
    const { data: leadData, error: leadError } = await supabase
      .from('leads')
      .insert([
        {
          contact_id: contactId,
          title: `Check-up: ${contact.company || contact.full_name || 'Nuovo'}`,
          stage: 'scheduled',
          org_id: orgId,
        },
      ])
      .select('id')
      .single();

    if (leadError) {
      alert(`Errore creazione lead: ${leadError.message}`);
      return;
    }

    // Update contact prospect_status
    await supabase
      .from('contacts')
      .update({ prospect_status: null }) // Remove from prospecting
      .eq('id', contactId)
      .eq('org_id', orgId);

    setShowCheckupModal(null);
    navigate(`/crm/leads/${leadData.id}`);
  }

  const formatRelative = useCallback((iso?: string | null) => {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const day = 1000 * 60 * 60 * 24;
    if (diff < day) return 'Oggi';
    if (diff < day * 2) return 'Ieri';
    const days = Math.round(diff / day);
    return `${days} giorni fa`;
  }, []);

  const openNewProspectDrawer = () => {
    setEditingProspect(null);
    setProspectForm({
      company: '',
      full_name: '',
      phone: '',
      email: '',
      company_address: '',
      company_city: '',
      company_zip: '',
      google_maps_url: '',
      website: '',
      source: 'cold_outreach',
      prospect_status: 'new',
      lifecycle_stage: 'contact',
      tags: '',
    });
    setContactSearch('');
    setContactOptions([]);
    setSelectedContacts([]);
    setShowContactSearch(false);
    setShowProspectDrawer(true);
  };

  const openEditProspectDrawer = (contact: ProspectContact) => {
    setEditingProspect(contact);
    setProspectForm({
      company: contact.company || '',
      full_name: contact.full_name || '',
      phone: contact.phone || '',
      email: contact.email || '',
      company_address: contact.company_address || '',
      company_city: contact.company_city || '',
      company_zip: contact.company_zip || '',
      google_maps_url: contact.google_maps_url || '',
      website: (contact as any).website || '',
      source: contact.source || 'cold_outreach',
      prospect_status: contact.prospect_status || 'new',
      lifecycle_stage: (contact as any).lifecycle_stage || 'contact',
      tags: Array.isArray((contact as any).tags) ? (contact as any).tags.join(', ') : '',
    });
    setShowProspectDrawer(true);
  };

  // Function to find coordinates from Maps URL
  const findCoordinates = useCallback(async (url: string) => {
    if (!url || url.trim().length === 0) {
      setCoordinatesMessage({ type: 'error', text: 'Inserisci un URL Google Maps valido' });
      setTimeout(() => setCoordinatesMessage({ type: null, text: '' }), 3000);
      return;
    }

    setMapsUrlExpanding(true);
    setCoordinatesMessage({ type: null, text: '' });

    try {
      // First try to extract coordinates directly (for non-short URLs)
      let coords = extractCoordinatesFromUrl(url);
      let finalUrl = url;

      // If no coordinates found, try expanding the URL (for short URLs)
      if (!coords) {
        const response = await fetch('/api/utils/expand-maps-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url })
        });
        const data = await response.json();

        if (data.success && data.lat && data.lng) {
          coords = { lat: data.lat, lng: data.lng };
          finalUrl = data.longUrl || url;
        } else {
          throw new Error(data.error || 'Could not extract coordinates');
        }
      }

      if (coords) {
        // Update form state
        setProspectForm({
          ...prospectForm,
          google_maps_url: finalUrl
        });

        // If editing, save coordinates to database
        if (editingProspect && orgId) {
          await supabase
            .from('contacts')
            .update({ 
              lat: coords.lat, 
              lng: coords.lng,
              google_maps_url: finalUrl
            })
            .eq('id', editingProspect.id)
            .eq('org_id', orgId);
        }

        // Show success message
        setCoordinatesMessage({ 
          type: 'success', 
          text: `Posizione trovata: ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}` 
        });
        setTimeout(() => setCoordinatesMessage({ type: null, text: '' }), 5000);
        setMapsUrlExpanded(true);
      } else {
        throw new Error('Could not extract coordinates');
      }
    } catch (error: any) {
      console.error('[Prospezione] Error finding coordinates:', error);
      setCoordinatesMessage({ 
        type: 'error', 
        text: 'Impossibile estrarre le coordinate. Usa un link Google Maps valido.' 
      });
      setTimeout(() => setCoordinatesMessage({ type: null, text: '' }), 5000);
    } finally {
      setMapsUrlExpanding(false);
    }
  }, [prospectForm, editingProspect, orgId]);

  async function saveProspect() {
    if (!canEdit || !orgId) return;
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;

    // If we have selected contacts, convert them to prospects
    // Note: updated_at column doesn't exist, so we only update prospect_status and lifecycle_stage
    // If lifecycle_stage is 'contact', clear prospect_status (move back to Contacts screen)
    if (selectedContacts.length > 0 && !editingProspect) {
      const finalProspectStatus = prospectForm.lifecycle_stage === 'contact' ? null : prospectForm.prospect_status;
      const { error } = await supabase
        .from('contacts')
        .update({ 
          prospect_status: finalProspectStatus,
          lifecycle_stage: prospectForm.lifecycle_stage,
        })
        .in('id', selectedContacts.map(c => c.id))
        .eq('org_id', orgId);

      if (error) {
        alert(`Errore aggiornamento contatti: ${error.message}`);
        return;
      }

      setShowProspectDrawer(false);
      setSelectedContacts([]);
      setContactSearch('');
      await fetchContacts();
      return;
    }

    // Otherwise, create/update single prospect
    const tagsArray = prospectForm.tags ? prospectForm.tags.split(',').map(s => s.trim()).filter(Boolean) : null;

    // Extract coordinates from Google Maps URL if present
    let lat: number | null = null;
    let lng: number | null = null;
    if (prospectForm.google_maps_url) {
      const coords = extractCoordinatesFromUrl(prospectForm.google_maps_url);
      if (coords) {
        lat = coords.lat;
        lng = coords.lng;
      }
    }

    // If lifecycle_stage is 'contact', clear prospect_status (move back to Contacts screen)
    const finalProspectStatus = prospectForm.lifecycle_stage === 'contact' ? null : prospectForm.prospect_status;

    const payload: any = {
      company: prospectForm.company || null,
      full_name: prospectForm.full_name || null,
      phone: prospectForm.phone || null,
      email: prospectForm.email || null,
      company_address: prospectForm.company_address || null,
      company_city: prospectForm.company_city || null,
      company_zip: prospectForm.company_zip || null,
      google_maps_url: prospectForm.google_maps_url || null,
      source: prospectForm.source || 'cold_outreach',
      prospect_status: finalProspectStatus,
      org_id: orgId,
      created_by: uid || null,
      lifecycle_stage: prospectForm.lifecycle_stage,
      last_activity_at: new Date().toISOString(),
      // Note: updated_at column doesn't exist, so we don't include it in the payload
    };
    
    // Add coordinates if extracted
    if (lat != null && lng != null) {
      payload.lat = lat;
      payload.lng = lng;
    }

    if (tagsArray && tagsArray.length > 0) {
      payload.tags = tagsArray;
    }

    // Try to add website if column exists
    try {
      payload.website = prospectForm.website || null;
    } catch {
      // Website column might not exist
    }

    if (editingProspect) {
      // Update existing prospect
      const { error } = await supabase
        .from('contacts')
        .update(payload)
        .eq('id', editingProspect.id)
        .eq('org_id', orgId);

      if (error) {
        alert(`Errore aggiornamento: ${error.message}`);
        return;
      }
    } else {
      // Create new prospect
      const { error } = await supabase.from('contacts').insert([payload]);
      if (error) {
        alert(`Errore creazione: ${error.message}`);
        return;
      }
    }

    setShowProspectDrawer(false);
    setEditingProspect(null);
    setSelectedContacts([]);
    setContactSearch('');
    await fetchContacts();
  }

  // Early return must be AFTER all hooks are defined
  if (loading) {
    return <div className="text-center p-6">{t('crm.common.loading')}</div>;
  }

  return (
    <div className="space-y-4 lg:space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Header with Title and Primary Action */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('crm.prospezione.title')}</h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">{t('crm.prospezione.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Search Bar */}
          <div className="relative flex-1 sm:flex-initial sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Cerca prospect..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Tag Filter Dropdown */}
          <div className="relative flex-1 sm:flex-initial sm:min-w-[200px]">
            <button
              type="button"
              onClick={() => setTagsDropdownOpen(!tagsDropdownOpen)}
              className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-3 py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 text-left flex items-center justify-between min-h-[38px]"
            >
              <span className="truncate">
                {selectedTags.length === 0
                  ? t('crm.common.filterTag')
                  : selectedTags.length === 1
                  ? selectedTags[0]
                  : t('crm.common.selected', { count: selectedTags.length })}
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${tagsDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {tagsDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full min-w-[250px] max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl">
                {tags.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">Nessun tag disponibile</div>
                ) : (
                  <>
                    {tags.map((tag) => {
                      const isSelected = selectedTags.includes(tag);
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedTags(selectedTags.filter(t => t !== tag));
                            } else {
                              setSelectedTags([...selectedTags, tag]);
                            }
                          }}
                          className={`w-full text-left px-3 py-2 text-xs sm:text-sm transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex items-center gap-2 ${
                            isSelected
                              ? 'bg-cyan-50 dark:bg-cyan-900/30 hover:bg-cyan-100 dark:hover:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="flex-1">{tag}</span>
                        </button>
                      );
                    })}
                    {selectedTags.length > 0 && (
                      <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTags([]);
                            setTagsDropdownOpen(false);
                          }}
                          className="w-full px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          Cancella tutti
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          {canEdit && (
            <button
              onClick={openNewProspectDrawer}
              className="px-4 sm:px-5 py-2.5 rounded-lg bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors flex items-center gap-2 text-sm shadow-md hover:shadow-lg"
            >
              <Plus className="w-5 h-5" />
              <span>Aggiungi Prospect</span>
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('crm.prospezione.kpi.totalProspects')}</div>
          <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">{prospectKpis.total}</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('crm.prospezione.kpi.distributed')}: {stageTotals.new} {t('crm.prospezione.stages.new')} · {stageTotals.to_visit} {t('crm.prospezione.stages.to_visit')} · {stageTotals.visited_interested} {t('crm.prospezione.stages.visited_interested')} · {stageTotals.visited_not_interested} {t('crm.prospezione.stages.visited_not_interested')} · {stageTotals.nurture} {t('crm.prospezione.stages.nurture')} · {stageTotals.disqualified} {t('crm.prospezione.stages.disqualified')}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('crm.prospezione.kpi.conversionRate')}</div>
          <div className="mt-2 text-2xl font-semibold text-emerald-600 dark:text-emerald-400">{prospectKpis.conversionRate}%</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('crm.prospezione.kpi.interestedOutOf', { interested: prospectKpis.interested, total: prospectKpis.total || 1 })}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('crm.prospezione.kpi.visits')}</div>
          <div className="mt-2 flex items-center gap-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            <MapPin className="w-5 h-5 text-sky-600 dark:text-sky-400" />
            {prospectKpis.visited}
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('crm.prospezione.kpi.completed')}: {prospectKpis.visited} · {t('crm.prospezione.kpi.toVisit')}: {prospectKpis.toVisit}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{t('crm.prospezione.kpi.followUpDue')}</div>
          {prospectKpis.needsFollowUp.length === 0 ? (
            <div className="mt-3 text-sm text-emerald-600 dark:text-emerald-400">{t('crm.prospezione.kpi.allUpToDate')}</div>
          ) : (
            <ul className="mt-2 space-y-2 text-xs text-gray-700 dark:text-gray-300">
              {prospectKpis.needsFollowUp.map((prospect) => (
                <li key={prospect.id} className="flex items-center justify-between">
                  <span className="truncate">{prospect.company || prospect.full_name || t('crm.prospezione.noName')}</span>
                  <span className="text-gray-500 dark:text-gray-400">{formatRelative(prospect.updated_at || prospect.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Mobile: Filter Chip Row */}
      <div className="lg:hidden mb-4 px-4">
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <button
            onClick={() => setSelectedStageFilter('all')}
            className={`flex-shrink-0 px-4 py-2 rounded-full font-semibold text-sm min-h-[44px] transition-all ${
              selectedStageFilter === 'all'
                ? 'bg-cyan-500 dark:bg-cyan-600 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            }`}
          >
            Tutti ({contacts.length})
          </button>
          {prospectStages.map((stage) => (
            <button
              key={stage}
              onClick={() => setSelectedStageFilter(stage)}
              className={`flex-shrink-0 px-4 py-2 rounded-full font-semibold text-sm min-h-[44px] transition-all ${
                selectedStageFilter === stage
                  ? 'bg-cyan-500 dark:bg-cyan-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {stageLabels[stage]} ({stageTotals[stage]})
            </button>
          ))}
        </div>
      </div>

      {/* Desktop: Kanban Columns (Hidden on Mobile) */}
      <div 
        className="hidden lg:block overflow-x-auto -mx-4 lg:mx-0 pb-4 lg:pb-0" 
        style={{ 
          WebkitOverflowScrolling: 'touch', 
          scrollbarWidth: 'thin',
          msOverflowStyle: '-ms-autohiding-scrollbar'
        }}
      >
        <div className="grid lg:grid-cols-3 xl:grid-cols-6 gap-4 px-4 lg:px-0">
        {prospectStages.map((stage) => {
          const colors = getStageColors(stage);
          return (
          <div
            key={stage}
            className={`border rounded-lg p-3 flex flex-col min-h-[320px] w-[280px] sm:w-[300px] lg:w-auto transition-colors snap-start lg:snap-none ${
              dragOverStage === stage 
                ? 'border-cyan-500 dark:border-cyan-400 bg-cyan-50 dark:bg-cyan-900/30' 
                : `${colors.border} ${colors.bg}`
            }`}
            onDragOver={(event) => handleDragOver(stage, event)}
            onDrop={() => handleDrop(stage)}
            onDragLeave={() => handleDragLeave(stage)}
          >
            <div className={`font-semibold mb-2 flex items-center gap-2 text-sm px-2 py-1.5 rounded ${colors.headerBg} ${colors.text}`}>
              <GripVertical className="w-4 h-4 flex-shrink-0 opacity-70" />
              <span className="truncate">{stageLabels[stage]}</span>
              <span className="text-xs opacity-80 flex-shrink-0">{stageTotals[stage]}</span>
            </div>
            <div className="flex flex-col gap-2 min-h-[200px]">
              {grouped[stage].map((contact) => (
                <div
                  key={contact.id}
                  className={`p-2.5 sm:p-3 rounded-lg border bg-white dark:bg-gray-800 space-y-1.5 sm:space-y-2 transition-opacity shadow-sm ${
                    draggingId === contact.id ? 'opacity-70 border-cyan-500 dark:border-cyan-400' : 'border-gray-200 dark:border-gray-700'
                  }`}
                  draggable={canEdit}
                  onDragStart={() => handleDragStart(contact.id)}
                  onDragEnd={handleDragEnd}
                >
                  <div className="font-semibold text-gray-900 dark:text-gray-100 text-xs sm:text-sm line-clamp-2">
                    {contact.company || contact.full_name || t('crm.prospezione.noName')}
                  </div>
                  {contact.full_name && contact.company && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {contact.full_name}
                    </div>
                  )}
                  {contact.company_address && (
                    <div className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span className="line-clamp-1">{contact.company_address}{contact.company_city && `, ${contact.company_city}`}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    {contact.google_maps_url && (
                      <a
                        href={contact.google_maps_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 flex items-center gap-1"
                      >
                        <MapPin className="w-3 h-3" />
                        Maps
                      </a>
                    )}
                    {(contact as any).website && (
                      <a
                        href={(contact as any).website.startsWith('http') ? (contact as any).website : `https://${(contact as any).website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 flex items-center gap-1"
                      >
                        <Globe className="w-3 h-3" />
                        Website
                      </a>
                    )}
                  </div>
                  {(contact as any).tags && Array.isArray((contact as any).tags) && (contact as any).tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {(contact as any).tags.slice(0, 2).map((tag: string) => (
                        <span key={tag} className="inline-flex items-center rounded-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-1.5 py-0.5 text-[10px] text-gray-700 dark:text-gray-300">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  {contact.phone && (
                    <div className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                      <span>📞</span> {contact.phone}
                    </div>
                  )}
                  {contact.email && (
                    <div className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
                      <span>✉️</span> {contact.email}
                    </div>
                  )}
                  {contact.source && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Fonte: {contact.source}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formatRelative(contact.updated_at || contact.created_at)}
                  </div>
                  {canEdit && (
                    <div className="flex flex-col gap-1.5 mt-2">
                      <button
                        onClick={() => openEditProspectDrawer(contact)}
                        className="w-full px-3 py-1.5 rounded-lg border border-cyan-500 dark:border-cyan-400 text-cyan-600 dark:text-cyan-400 font-semibold hover:bg-cyan-50 dark:hover:bg-cyan-900/30 transition-colors text-xs flex items-center justify-center gap-1.5"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Modifica
                      </button>
                      {stage === 'visited_interested' && (
                        <button
                          onClick={() => setShowCheckupModal(contact.id)}
                          className="w-full px-3 py-1.5 rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors text-xs shadow-md"
                        >
                          {t('crm.prospezione.createCheckup')}
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/crm/contacts/${contact.id}`)}
                        className="w-full px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        {t('crm.prospezione.details')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {grouped[stage].length === 0 && (
                <div className="p-3 border border-dashed border-gray-300 dark:border-gray-600 rounded text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 text-center">
                  {t('crm.prospezione.noContacts')}
                </div>
              )}
            </div>
          </div>
          );
        })}
        </div>
      </div>

      {/* Mobile: Vertical List (Only on Mobile) */}
      <div className="lg:hidden space-y-3 pb-20">
        {(selectedStageFilter === 'all'
          ? contacts.filter(contact => {
              if (!contact.prospect_status) return false;
              const searchLower = searchTerm.trim().toLowerCase();
              if (searchLower) {
                const companyMatch = contact.company?.toLowerCase().includes(searchLower) ?? false;
                const fullNameMatch = contact.full_name?.toLowerCase().includes(searchLower) ?? false;
                if (!companyMatch && !fullNameMatch) return false;
              }
              if (selectedTags.length > 0) {
                const firstTag = (contact.tags || [])[0];
                if (!firstTag) return false;
                const tagMatches = selectedTags.some(selectedTag => 
                  firstTag.toLowerCase() === selectedTag.toLowerCase()
                );
                if (!tagMatches) return false;
              }
              return true;
            })
          : grouped[selectedStageFilter]
        ).map((contact) => {
          // Get border color from status
          let borderColor = '#06b6d4'; // cyan default (new)
          if (contact.prospect_status === 'to_visit') borderColor = '#2563eb'; // blue
          else if (contact.prospect_status === 'visited_interested') borderColor = '#f97316'; // orange
          else if (contact.prospect_status === 'visited_not_interested') borderColor = '#9ca3af'; // gray
          else if (contact.prospect_status === 'nurture') borderColor = '#a855f7'; // purple
          else if (contact.prospect_status === 'disqualified') borderColor = '#64748b'; // slate
          
          return (
            <div
              key={contact.id}
              className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm active:scale-[0.98] transition-transform"
              style={{ borderLeftColor: borderColor, borderLeftWidth: '4px' }}
            >
              {/* Company Name - Large, Bold */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 truncate">
                {contact.company || contact.full_name || t('crm.prospezione.noName')}
              </h3>
              
              {/* Secondary Info */}
              {contact.full_name && contact.company && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {contact.full_name}
                </p>
              )}
              
              {/* Phone */}
              {contact.phone && (
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 mb-2">
                  <span className="text-lg">📞</span>
                  <span className="text-sm">{contact.phone}</span>
                </div>
              )}
              
              {/* Address */}
              {contact.company_address && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 mb-2">
                  <MapPin className="w-4 h-4" />
                  <span className="truncate">{contact.company_address}{contact.company_city && `, ${contact.company_city}`}</span>
                </div>
              )}
              
              {/* Actions */}
              {canEdit && (
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => openEditProspectDrawer(contact)}
                    className="flex-1 px-4 py-3 rounded-lg border-2 border-cyan-500 dark:border-cyan-400 text-cyan-600 dark:text-cyan-400 font-semibold hover:bg-cyan-50 dark:hover:bg-cyan-900/30 transition-colors text-sm min-h-[44px]"
                  >
                    Modifica
                  </button>
                  <button
                    onClick={() => navigate(`/crm/contacts/${contact.id}`)}
                    className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm min-h-[44px]"
                  >
                    Dettagli
                  </button>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Empty State */}
        {(selectedStageFilter === 'all'
          ? contacts.filter(contact => {
              if (!contact.prospect_status) return false;
              const searchLower = searchTerm.trim().toLowerCase();
              if (searchLower) {
                const companyMatch = contact.company?.toLowerCase().includes(searchLower) ?? false;
                const fullNameMatch = contact.full_name?.toLowerCase().includes(searchLower) ?? false;
                if (!companyMatch && !fullNameMatch) return false;
              }
              if (selectedTags.length > 0) {
                const firstTag = (contact.tags || [])[0];
                if (!firstTag) return false;
                const tagMatches = selectedTags.some(selectedTag => 
                  firstTag.toLowerCase() === selectedTag.toLowerCase()
                );
                if (!tagMatches) return false;
              }
              return true;
            }).length === 0
          : grouped[selectedStageFilter].length === 0
        ) && (
          <div className="p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-center bg-gray-50 dark:bg-gray-700/50">
            <p className="text-gray-500 dark:text-gray-400">{t('crm.prospezione.noContacts')}</p>
          </div>
        )}
      </div>

      {/* Prospect Drawer */}
      {showProspectDrawer && canEdit && (
        <div className="fixed inset-0 bg-black/60 grid backdrop-blur-sm z-50">
          <div className="ml-auto h-full w-full lg:max-w-md bg-white dark:bg-gray-800 p-4 sm:p-6 overflow-auto border-l border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {editingProspect ? 'Modifica Prospect' : 'Nuovo Prospect'}
              </h2>
              <button 
                onClick={() => { setShowProspectDrawer(false); setEditingProspect(null); }} 
                className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                ✕
              </button>
            </div>
            <div className="grid gap-3">
              {/* Contact Search Section - Only for new prospects */}
              {!editingProspect && (
                <div className="space-y-2 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      Cerca Contatti Esistenti
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowContactSearch(!showContactSearch)}
                      className="text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-500"
                    >
                      {showContactSearch ? 'Nascondi' : 'Mostra'}
                    </button>
                  </div>
                  {showContactSearch && (
                    <div className="space-y-2">
                      <div className="relative">
                        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2">
                          <Search className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          <input
                            className="bg-transparent flex-1 focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                            placeholder="Cerca per nome, azienda, email o telefono..."
                            value={contactSearch}
                            onChange={(e) => {
                              setContactSearch(e.target.value);
                            }}
                          />
                          {contactLoading && <span className="text-xs text-gray-500">Caricamento...</span>}
                        </div>
                        {contactOptions.length > 0 && (
                          <div className="absolute z-30 mt-2 w-full max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl">
                            {contactOptions.map((option) => {
                              const isSelected = selectedContacts.some(c => c.id === option.id);
                              return (
                                <button
                                  key={option.id}
                                  type="button"
                                  onClick={() => {
                                    if (isSelected) {
                                      setSelectedContacts(selectedContacts.filter(c => c.id !== option.id));
                                    } else {
                                      setSelectedContacts([...selectedContacts, option]);
                                    }
                                    setContactSearch('');
                                    setContactOptions([]);
                                  }}
                                  className={`w-full text-left px-3 py-2.5 text-sm transition border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                                    isSelected 
                                      ? 'bg-cyan-50 dark:bg-cyan-900/30 hover:bg-cyan-100 dark:hover:bg-cyan-900/40' 
                                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                        {option.company || option.full_name || option.email || 'Senza nome'}
                                        {isSelected && <CheckCircle2 className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />}
                                      </div>
                                      <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 flex gap-2 flex-wrap">
                                        {option.full_name && option.company && <span>{option.full_name}</span>}
                                        {option.email && <span>{option.email}</span>}
                                        {option.phone && <span>{option.phone}</span>}
                                        {option.company_city && <span>{option.company_city}</span>}
                                      </div>
                                      {option.tags && option.tags.length > 0 && (
                                        <div className="mt-1 flex flex-wrap gap-1">
                                          {option.tags.slice(0, 2).map((tag: string) => (
                                            <span key={tag} className="inline-flex items-center gap-1 rounded-full border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 px-1.5 py-0.5 text-[10px] text-gray-700 dark:text-gray-300">
                                              <Tags className="w-2.5 h-2.5" /> {tag}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {selectedContacts.length > 0 && (
                        <div className="space-y-2">
                          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                            Contatti selezionati ({selectedContacts.length})
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedContacts.map((contact) => (
                              <div
                                key={contact.id}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-cyan-50 dark:bg-cyan-900/30 border border-cyan-200 dark:border-cyan-700 text-sm"
                              >
                                <span className="text-gray-900 dark:text-gray-100 font-medium">
                                  {contact.company || contact.full_name || contact.email || 'Senza nome'}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setSelectedContacts(selectedContacts.filter(c => c.id !== contact.id))}
                                  className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-500"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Divider - Only show if no contacts are selected */}
              {!editingProspect && selectedContacts.length === 0 && (
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white dark:bg-gray-800 px-2 text-gray-500 dark:text-gray-400">oppure</span>
                  </div>
                </div>
              )}

              {/* Manual Entry Fields - Only show if no contacts are selected */}
              {selectedContacts.length === 0 && (
                <>
                  <input
                    placeholder="Ragione Sociale *"
                    className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                    value={prospectForm.company}
                    onChange={e => setProspectForm({ ...prospectForm, company: e.target.value })}
                  />
                  <input
                    placeholder="Nome Contatto"
                    className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                    value={prospectForm.full_name}
                    onChange={e => setProspectForm({ ...prospectForm, full_name: e.target.value })}
                  />
                  <input
                    placeholder="Email"
                    className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                    value={prospectForm.email}
                    onChange={e => setProspectForm({ ...prospectForm, email: e.target.value })}
                  />
                  <input
                    placeholder="Telefono"
                    className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                    value={prospectForm.phone}
                    onChange={e => setProspectForm({ ...prospectForm, phone: e.target.value })}
                  />
                  <input
                    placeholder="Indirizzo"
                    className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                    value={prospectForm.company_address}
                    onChange={e => setProspectForm({ ...prospectForm, company_address: e.target.value })}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      placeholder="Città"
                      className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                      value={prospectForm.company_city}
                      onChange={e => setProspectForm({ ...prospectForm, company_city: e.target.value })}
                    />
                    <input
                      placeholder="CAP"
                      className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                      value={prospectForm.company_zip}
                      onChange={e => setProspectForm({ ...prospectForm, company_zip: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="relative flex items-center gap-2">
                      <input
                        placeholder="Link Google Maps"
                        className="flex-1 bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 pr-10 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                        value={prospectForm.google_maps_url}
                        onChange={async (e) => {
                          const url = e.target.value;
                          setMapsUrlExpanded(false);
                          setCoordinatesMessage({ type: null, text: '' });
                          setProspectForm({ ...prospectForm, google_maps_url: url });
                          
                          // Check if it's a short URL that needs expansion
                          if (url && url.length > 10 && isShortMapsUrl(url)) {
                            setMapsUrlExpanding(true);
                            try {
                              const response = await fetch('/api/utils/expand-maps-url', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ url })
                              });
                              const data = await response.json();
                              
                              if (data.success && data.lat && data.lng) {
                                // Update form with expanded URL
                                setProspectForm({ 
                                  ...prospectForm, 
                                  google_maps_url: data.longUrl || url 
                                });
                                
                                // If editing, save coordinates to database
                                if (editingProspect && orgId) {
                                  await supabase
                                    .from('contacts')
                                    .update({ 
                                      lat: data.lat, 
                                      lng: data.lng,
                                      google_maps_url: data.longUrl || url
                                    })
                                    .eq('id', editingProspect.id)
                                    .eq('org_id', orgId);
                                }
                                
                                setMapsUrlExpanded(true);
                                setCoordinatesMessage({ 
                                  type: 'success', 
                                  text: `Posizione trovata: ${data.lat.toFixed(6)}, ${data.lng.toFixed(6)}` 
                                });
                                setTimeout(() => {
                                  setMapsUrlExpanded(false);
                                  setCoordinatesMessage({ type: null, text: '' });
                                }, 5000);
                              }
                            } catch (error) {
                              console.error('[Prospezione] Error expanding URL:', error);
                            } finally {
                              setMapsUrlExpanding(false);
                            }
                          } else if (editingProspect && url && url.length > 20 && orgId) {
                            // Auto-extract and save coordinates when URL is pasted (non-short URL, for editing)
                            const coords = extractCoordinatesFromUrl(url);
                            if (coords) {
                              // Save coordinates to database
                              await supabase
                                .from('contacts')
                                .update({ lat: coords.lat, lng: coords.lng })
                                .eq('id', editingProspect.id)
                                .eq('org_id', orgId);
                              setCoordinatesMessage({ 
                                type: 'success', 
                                text: `Posizione trovata: ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}` 
                              });
                              setTimeout(() => setCoordinatesMessage({ type: null, text: '' }), 5000);
                            }
                          }
                        }}
                        onBlur={async (e) => {
                          const url = e.target.value;
                          if (url && url.trim().length > 0) {
                            // Check if coordinates are missing (for editing prospects, check database)
                            if (editingProspect) {
                              // For editing, check if we need to extract coordinates
                              const coords = extractCoordinatesFromUrl(url);
                              if (!coords) {
                                // Try expanding if it's a short URL or couldn't extract
                                await findCoordinates(url);
                              }
                            } else {
                              // For new prospects, auto-trigger if we don't have coordinates extracted
                              const coords = extractCoordinatesFromUrl(url);
                              if (!coords && url === prospectForm.google_maps_url) {
                                await findCoordinates(url);
                              }
                            }
                          }
                        }}
                      />
                      {/* Find Coordinates Button */}
                      {prospectForm.google_maps_url && (
                        <button
                          type="button"
                          onClick={() => {
                            if (prospectForm.google_maps_url) {
                              findCoordinates(prospectForm.google_maps_url);
                            }
                          }}
                          disabled={mapsUrlExpanding}
                          className="flex-shrink-0 p-2 rounded-lg border border-cyan-500 dark:border-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Trova Coordinate"
                        >
                          {mapsUrlExpanding ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                    {/* Success/Error Message */}
                    {coordinatesMessage.type && (
                      <div className={`text-xs px-2 py-1 rounded ${
                        coordinatesMessage.type === 'success' 
                          ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
                          : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                      }`}>
                        {coordinatesMessage.text}
                      </div>
                    )}
                    {/* Legacy success indicator (if still needed) */}
                    {mapsUrlExpanded && !mapsUrlExpanding && !coordinatesMessage.type && (
                      <div className="text-xs text-green-600 dark:text-green-400">
                        📍 Coordinate trovate
                      </div>
                    )}
                  </div>
                  <input
                    placeholder="Sito Web"
                    className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                    value={prospectForm.website}
                    onChange={e => setProspectForm({ ...prospectForm, website: e.target.value })}
                  />
                  <input
                    placeholder="Fonte"
                    className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                    value={prospectForm.source === 'cold_outreach' ? 'Cold Outreach' : prospectForm.source}
                    onChange={e => setProspectForm({ ...prospectForm, source: e.target.value })}
                  />
                </>
              )}
              {/* Prospect Status & Lifecycle */}
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Stato Prospect</label>
                <select
                  className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500 w-full"
                  value={prospectForm.prospect_status || 'new'}
                  onChange={e => setProspectForm({ ...prospectForm, prospect_status: e.target.value as ProspectStatus })}
                  disabled={!prospectForm.prospect_status} // Disable if not a prospect
                >
                  {prospectStages.map((stage) => (
                    <option key={stage} value={stage}>
                      {stageLabels[stage]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Tipo Contatto</label>
                <select
                  className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 focus:ring-cyan-500 focus:border-cyan-500 w-full"
                  value={prospectForm.prospect_status ? 'prospect' : prospectForm.lifecycle_stage}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === 'prospect') {
                      // Ensure it stays/becomes a prospect
                      setProspectForm({ 
                        ...prospectForm, 
                        lifecycle_stage: 'contact', // Underlying stage is contact
                        prospect_status: prospectForm.prospect_status || 'new' // Ensure status exists
                      });
                    } else {
                      // Changing to Contact, Lead or Client implies NOT being a prospect anymore?
                      // Or just changing the underlying stage?
                      // Usually if you select Client, you are not a prospect anymore.
                      // If you select Lead, you might still be a prospect?
                      // Let's assume selecting from this list sets the specific type.
                      
                      // If choosing Contact/Lead/Client, we might want to clear prospect_status 
                      // IF the user intends to move it out of prospects. 
                      // But this is the "Modifica Prospect" form.
                      // If they change it here, it might disappear from this list!
                      
                      setProspectForm({ 
                        ...prospectForm, 
                        lifecycle_stage: val as 'contact' | 'lead' | 'client',
                        // If they explicitly choose Contact/Lead/Client, do we clear prospect_status?
                        // If they choose 'prospect', we keep it.
                        // If they choose 'client', it's definitely not a prospect anymore.
                        prospect_status: val === 'client' || val === 'contact' ? null : prospectForm.prospect_status 
                        // Wait, if they choose 'contact', they might mean "Just a contact, not a prospect".
                      });
                    }
                  }}
                >
                  <option value="prospect">Prospect</option>
                  <option value="contact">{t('crm.common.stages.contact')}</option>
                  <option value="lead">{t('crm.common.stages.lead')}</option>
                  <option value="client">{t('crm.common.stages.client')}</option>
                </select>
              </div>
              <input
                placeholder="Tag (separati da virgola)"
                className="bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-cyan-500 focus:border-cyan-500"
                value={prospectForm.tags}
                onChange={e => setProspectForm({ ...prospectForm, tags: e.target.value })}
              />
              <div className="flex justify-end gap-3 mt-4">
                <button 
                  onClick={() => { setShowProspectDrawer(false); setEditingProspect(null); }} 
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Annulla
                </button>
                <button 
                  onClick={saveProspect} 
                  className="px-4 py-2 rounded-lg bg-cyan-500 text-white font-semibold hover:bg-cyan-600 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!editingProspect && selectedContacts.length === 0 && !prospectForm.company}
                >
                  {editingProspect 
                    ? 'Salva' 
                    : selectedContacts.length > 0 
                      ? `Importa ${selectedContacts.length} Prospect` 
                      : 'Crea'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCheckupModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('crm.prospezione.createCheckupTitle')}</h3>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              {t('crm.prospezione.createCheckupMessage')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCheckupModal(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {t('crm.prospezione.createCheckupCancel')}
              </button>
              <button
                onClick={() => createCheckup(showCheckupModal)}
                className="px-4 py-2 rounded-lg bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors shadow-md"
              >
                {t('crm.prospezione.createCheckupConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProspezionePage;

